import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './AboutUsHistoryForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsHistoryForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    historyItems: [],
    whyStarted: { headline: '', paragraph: '', tags: [], note: '' },
    established: { date: '', facts: [], cta: '' }
  });

  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionIds, setSectionIds] = useState({ history: null, whyStarted: null, established: null });
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [activeTab, setActiveTab] = useState('history');
  const [editingHistoryIndex, setEditingHistoryIndex] = useState(null);
  const [modalHistoryData, setModalHistoryData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [newTag, setNewTag] = useState('');
  const [newFact, setNewFact] = useState({ label: '', value: '' });

  useEffect(() => {
    loadMediaMap();
    loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsHistoryForm] Media fetch error:', err.message);
    }
  };

  const loadSections = async () => {
    setLoading(true);
    try {
      let historySection = null;
      let whyStartedSection = null;
      let establishedSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          historySection = result.data.find(s => s.section_type === 'about-us-history-shell');
          whyStartedSection = result.data.find(s => s.section_type === 'about-us-why-started-shell');
          establishedSection = result.data.find(s => s.section_type === 'about-us-established-shell');
        }
      }
      
      if (!historySection || !whyStartedSection || !establishedSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            if (!historySection) historySection = result.data.find(s => s.section_type === 'about-us-history-shell');
            if (!whyStartedSection) whyStartedSection = result.data.find(s => s.section_type === 'about-us-why-started-shell');
            if (!establishedSection) establishedSection = result.data.find(s => s.section_type === 'about-us-established-shell');
          }
        }
      }
      
      if (!historySection || !whyStartedSection || !establishedSection) {
        throw new Error('One or more sections not found');
      }

      setSectionIds({
        history: historySection.id,
        whyStarted: whyStartedSection.id,
        established: establishedSection.id
      });

      let newFormData = {
        historyItems: [],
        whyStarted: { headline: '', paragraph: '', tags: [], note: '' },
        established: { date: '', facts: [], cta: '' }
      };

      if (historySection.content_published) {
        let published = historySection.content_published;
        if (typeof published === 'string') published = JSON.parse(published);
        newFormData.historyItems = published.en?.items || [];
      }

      if (whyStartedSection.content_published) {
        let published = whyStartedSection.content_published;
        if (typeof published === 'string') published = JSON.parse(published);
        const en = published.en || {};
        newFormData.whyStarted = {
          headline: en.headline || '',
          paragraph: en.paragraph || '',
          tags: Array.isArray(en.tags) ? en.tags : [],
          note: en.note || ''
        };
      }

      if (establishedSection.content_published) {
        let published = establishedSection.content_published;
        if (typeof published === 'string') published = JSON.parse(published);
        const en = published.en || {};
        newFormData.established = {
          date: en.date || '',
          facts: Array.isArray(en.facts) ? en.facts : [],
          cta: en.cta || ''
        };
      }

      setFormData(newFormData);
      setOriginalData(newFormData);
    } catch (err) {
      console.error('[AboutUsHistoryForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHistoryItem = () => {
    setFormData(prev => ({
      ...prev,
      historyItems: [...prev.historyItems, { year: '', title: '', description: '', image: null }]
    }));
  };

  const handleDeleteHistoryItem = (itemIndex) => {
    const itemToDelete = originalData.historyItems[itemIndex];
    
    if (itemToDelete) {
      handleSubmitHistoryItemDelete(itemIndex, itemToDelete);
    }
    
    setFormData(prev => ({
      ...prev,
      historyItems: prev.historyItems.filter((_, idx) => idx !== itemIndex)
    }));
  };

  const handleSubmitHistoryItemDelete = async (itemIndex, item) => {
    if (!sectionIds.history) { toast.error('Section ID not loaded'); return; }

    setSubmitting(prev => ({ ...prev, [`hist_del_${itemIndex}`]: true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.history,
          field_id: `items.${itemIndex}.delete`,
          field_name: `Delete Timeline Item - ${item.year}`,
          old_value: item,
          new_value: null,
          locale: 'en'
        })
      });
      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Timeline item deleted`);
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`hist_del_${itemIndex}`]: false }));
    }
  };

  const handleHistoryItemChange = (field, value) => {
    setModalHistoryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    if (editingHistoryIndex === null && editingHistoryIndex !== 0) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [editingHistoryIndex]: true }));
    setImageProgress(prev => ({ ...prev, [editingHistoryIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [editingHistoryIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                handleHistoryItemChange('image', mediaId);
                toast.success('✓ Image uploaded');
              }
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed');
        }
        setImageUploading(prev => ({ ...prev, [editingHistoryIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [editingHistoryIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-history-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [editingHistoryIndex]: false }));
    }
  };

  const handleSubmitHistoryItem = async () => {
    if (!sectionIds.history) {
      toast.error('Section ID not loaded');
      return;
    }

    const itemIndex = editingHistoryIndex;
    const item = modalHistoryData;
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.year || !item.title || !item.description) {
      toast.warning('Please fill all required fields');
      return;
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionIds.history,
            field_id: `items.${itemIndex}.update`,
            field_name: `Update Timeline Item - ${item.year}`,
            old_value: originalData.historyItems[itemIndex] || {},
            new_value: item,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success('Timeline item submitted for approval');
        setFormData(prev => ({
          ...prev,
          historyItems: prev.historyItems.map((c, i) => i === itemIndex ? item : c)
        }));
        setOriginalData(prev => ({
          ...prev,
          historyItems: prev.historyItems.map((c, i) => i === itemIndex ? item : c)
        }));
        setEditingHistoryIndex(null);
        setModalHistoryData(null);
      } else {
        if (response.status === 409) {
          toast.error('This item already has a pending approval');
        } else {
          toast.error(result.error || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleWhyStartedChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      whyStarted: { ...prev.whyStarted, [field]: value }
    }));
  };

  const handleAddTag = () => {
    if (!newTag.trim()) { toast.warning('Tag cannot be empty'); return; }
    setFormData(prev => ({
      ...prev,
      whyStarted: { ...prev.whyStarted, tags: [...prev.whyStarted.tags, newTag.trim()] }
    }));
    setNewTag('');
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      whyStarted: { ...prev.whyStarted, tags: prev.whyStarted.tags.filter((_, i) => i !== index) }
    }));
  };

  const handleEstablishedChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      established: { ...prev.established, [field]: value }
    }));
  };

  const handleAddFact = () => {
    if (!newFact.label.trim() || !newFact.value.trim()) { toast.warning('Label and value cannot be empty'); return; }
    setFormData(prev => ({
      ...prev,
      established: { ...prev.established, facts: [...prev.established.facts, { label: newFact.label.trim(), value: newFact.value.trim() }] }
    }));
    setNewFact({ label: '', value: '' });
  };

  const handleRemoveFact = (index) => {
    setFormData(prev => ({
      ...prev,
      established: { ...prev.established, facts: prev.established.facts.filter((_, i) => i !== index) }
    }));
  };

  const handleSubmitWhyStarted = async () => {
    if (!sectionIds.whyStarted) { toast.error('Section ID not loaded'); return; }
    if (!formData.whyStarted.headline || !formData.whyStarted.paragraph) { toast.warning('Please fill headline and paragraph'); return; }

    setSubmitting(prev => ({ ...prev, 'why-started': true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.whyStarted,
          field_id: 'content.update',
          field_name: 'Update Why We Started',
          old_value: originalData.whyStarted,
          new_value: formData.whyStarted,
          locale: 'en'
        })
      });
      const result = await response.json();
      if (result.success || response.ok) {
        toast.success('Why We Started submitted for approval');
        setOriginalData(prev => ({ ...prev, whyStarted: formData.whyStarted }));
      } else {
        toast.error(result.error || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'why-started': false }));
    }
  };

  const handleSubmitEstablished = async () => {
    if (!sectionIds.established) { toast.error('Section ID not loaded'); return; }
    if (!formData.established.date) { toast.warning('Please fill established date'); return; }

    setSubmitting(prev => ({ ...prev, 'established': true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.established,
          field_id: 'content.update',
          field_name: 'Update Established',
          old_value: originalData.established,
          new_value: formData.established,
          locale: 'en'
        })
      });
      const result = await response.json();
      if (result.success || response.ok) {
        toast.success('Established submitted for approval');
        setOriginalData(prev => ({ ...prev, established: formData.established }));
      } else {
        toast.error(result.error || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'established': false }));
    }
  };

  if (loading) return <div className="history-form-container"><div className="loading">Loading sections...</div></div>;

  return (
    <div className="history-form-container">
      <ToastContainer />
      <div className="history-form">
        <div className="form-header">
          <h2>About Us - History, Why We Started & Established</h2>
          <p className="form-description">Manage three key sections about your institution</p>
        </div>

        {/* TAB NAVIGATION */}
        <div className="tabs-wrapper">
          <div className="tabs-nav">
            <button className={`tab-button ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
              <span className="tab-icon-svg">◆</span>
              <span>Timeline History</span>
            </button>
            <button className={`tab-button ${activeTab === 'whystarted' ? 'active' : ''}`} onClick={() => setActiveTab('whystarted')}>
              <span className="tab-icon-svg">⬤</span>
              <span>Why We Started</span>
            </button>
            <button className={`tab-button ${activeTab === 'established' ? 'active' : ''}`} onClick={() => setActiveTab('established')}>
              <span className="tab-icon-svg">■</span>
              <span>Established</span>
            </button>
          </div>
        </div>

        {/* TAB CONTENT */}
        <div className="tab-content">
          {/* HISTORY TAB - CARD BASED */}
          {activeTab === 'history' && (
            <div className="tab-pane">
              <div className="section-header">
                <h3>Timeline History</h3>
                <button type="button" onClick={handleAddHistoryItem} className="add-item-btn">+ Add Item</button>
              </div>
              {formData.historyItems.length === 0 ? (
                <div className="empty-state"><p>No timeline items yet. Click "Add Item" to create one.</p></div>
              ) : (
                <div className="cards-grid">
                  {formData.historyItems.map((item, idx) => (
                    <div key={idx} className="history-card" onClick={() => {
                      setEditingHistoryIndex(idx);
                      setModalHistoryData({...item});
                    }}>
                      {item.image && mediaMap[item.image] && (
                        <div className="card-image">
                          <img src={mediaMap[item.image]} alt={item.title} />
                        </div>
                      )}
                      <div className="card-content">
                        <div className="card-year">{item.year}</div>
                        <h4 className="card-title">{item.title}</h4>
                        <p className="card-desc">{item.description}</p>
                      </div>
                      <button type="button" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteHistoryItem(idx);
                      }} className="card-delete">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WHY WE STARTED TAB */}
          {activeTab === 'whystarted' && (
            <div className="tab-pane">
              <div className="section-header"><h3>Why We Started</h3></div>
              <div className="form-field-card"><label>Headline *</label>
                <input type="text" value={formData.whyStarted.headline || ''} onChange={(e) => handleWhyStartedChange('headline', e.target.value)} placeholder="Main headline..." className="text-input" /></div>
              <div className="form-field-card"><label>Paragraph *</label>
                <textarea value={formData.whyStarted.paragraph || ''} onChange={(e) => handleWhyStartedChange('paragraph', e.target.value)} placeholder="Your story..." className="textarea-input" rows="3" /></div>
              <div className="form-field-card"><label>Tags</label>
                <div className="add-item-section">
                  <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add tag..." className="text-input" onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }} />
                  <button type="button" onClick={handleAddTag} disabled={!newTag.trim()} className="add-item-submit-btn">Add</button>
                </div>
                {formData.whyStarted.tags.length > 0 && (
                  <div className="tags-list">
                    {formData.whyStarted.tags.map((tag, idx) => (
                      <div key={idx} className="tag-badge"><span>{tag}</span>
                        <button type="button" onClick={() => handleRemoveTag(idx)} className="remove-badge-btn">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-field-card"><label>Note</label>
                <textarea value={formData.whyStarted.note || ''} onChange={(e) => handleWhyStartedChange('note', e.target.value)} placeholder="Additional note..." className="textarea-input" rows="2" /></div>
              <button onClick={handleSubmitWhyStarted} disabled={submitting['why-started'] || !formData.whyStarted.headline} className="submit-btn">{submitting['why-started'] ? 'Submitting...' : 'Submit for Approval'}</button>
            </div>
          )}

          {/* ESTABLISHED TAB */}
          {activeTab === 'established' && (
            <div className="tab-pane">
              <div className="section-header"><h3>Established</h3></div>
              <div className="form-field-card"><label>Date *</label>
                <input type="text" value={formData.established.date || ''} onChange={(e) => handleEstablishedChange('date', e.target.value)} placeholder="e.g., July 2003" className="text-input" /></div>
              <div className="form-field-card"><label>Facts</label>
                <div className="add-fact-section">
                  <input type="text" value={newFact.label} onChange={(e) => setNewFact(prev => ({ ...prev, label: e.target.value }))} placeholder="Label" className="text-input" />
                  <input type="text" value={newFact.value} onChange={(e) => setNewFact(prev => ({ ...prev, value: e.target.value }))} placeholder="Value" className="text-input" />
                  <button type="button" onClick={handleAddFact} disabled={!newFact.label.trim() || !newFact.value.trim()} className="add-item-submit-btn">Add</button>
                </div>
                {formData.established.facts.length > 0 && (
                  <div className="facts-list">
                    {formData.established.facts.map((fact, idx) => (
                      <div key={idx} className="fact-card">
                        <div className="fact-content">
                          <span className="fact-label">{fact.label}</span>
                          <span className="fact-value">{fact.value}</span>
                        </div>
                        <button type="button" onClick={() => handleRemoveFact(idx)} className="remove-fact-btn">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-field-card"><label>Call to Action</label>
                <input type="text" value={formData.established.cta || ''} onChange={(e) => handleEstablishedChange('cta', e.target.value)} placeholder="CTA text..." className="text-input" /></div>
              <button onClick={handleSubmitEstablished} disabled={submitting['established'] || !formData.established.date} className="submit-btn">{submitting['established'] ? 'Submitting...' : 'Submit for Approval'}</button>
            </div>
          )}
        </div>

        {/* EDIT HISTORY ITEM MODAL */}
        {editingHistoryIndex !== null && modalHistoryData && (
          <div className="modal-overlay" onClick={() => {
            setEditingHistoryIndex(null);
            setModalHistoryData(null);
          }}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Timeline Item</h3>
                <button className="modal-close" onClick={() => {
                  setEditingHistoryIndex(null);
                  setModalHistoryData(null);
                }}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Year *</label>
                  <input type="text" value={modalHistoryData.year || ''} onChange={(e) => handleHistoryItemChange('year', e.target.value)} placeholder="e.g., 2012" className="text-input" />
                </div>

                <div className="modal-field">
                  <label>Title *</label>
                  <input type="text" value={modalHistoryData.title || ''} onChange={(e) => handleHistoryItemChange('title', e.target.value)} placeholder="Milestone title..." className="text-input" />
                </div>

                <div className="modal-field">
                  <label>Description *</label>
                  <textarea value={modalHistoryData.description || ''} onChange={(e) => handleHistoryItemChange('description', e.target.value)} placeholder="Describe this milestone..." className="textarea-input" rows="3" />
                </div>

                <div className="modal-field">
                  <label>Image</label>
                  {modalHistoryData.image && mediaMap[modalHistoryData.image] && (
                    <div className="modal-image-preview">
                      <img src={mediaMap[modalHistoryData.image]} alt="Timeline" />
                    </div>
                  )}
                  <div className="image-upload-area">
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} disabled={imageUploading[editingHistoryIndex]} className="image-input" id="modal-image-input" />
                    <label htmlFor="modal-image-input" className={`upload-label ${imageUploading[editingHistoryIndex] ? 'uploading' : ''}`}>
                      {imageUploading[editingHistoryIndex] ? `Uploading ${imageProgress[editingHistoryIndex]}%...` : 'Upload Image'}
                    </label>
                  </div>
                  <span className="hint">Max 5MB • Recommended: 288×288px • JPG, PNG, WebP</span>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => {
                  setEditingHistoryIndex(null);
                  setModalHistoryData(null);
                }}>Cancel</button>
                <button className="btn-submit" disabled={submitting[`item_${editingHistoryIndex}`] || !modalHistoryData.year || !modalHistoryData.title || !modalHistoryData.description} onClick={handleSubmitHistoryItem}>
                  {submitting[`item_${editingHistoryIndex}`] ? 'Submitting...' : 'Save & Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
